import { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import ChangeLine from '../../../components/novarium/ChangeLine'
import InfoTip from '../../../components/novarium/InfoTip'
import { UrgencyDailyPointDTO, UrgencyKpisDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { percentVariation, pointsVariation, Variation, Verdict, waitVerdict } from '../../ServicesStatistics/statisticsChange'
import { formatDuration } from '../../ServicesStatistics/periodDetail'
import { OpenAttentionNotice, UrgencyDayReference } from '../urgencyAnalysis'
import { formatCount, formatMinutes, formatPercent } from '../urgencySummaryFormat'
import { SectionStatus } from '../useSectionData'
import UrgencyInProgressNote from './UrgencyInProgressNote'

interface UrgencyDayKpisProps {
  /** Lo que se mira: sus llegadas y su espera se ven al instante (vienen del punto del gráfico o de la fila de la guardia). */
  day: UrgencyDailyPointDTO
  /** Indicadores de lo que se mira (`/summary` con desde = hasta); null mientras carga o si falló. */
  kpis: UrgencyKpisDTO | null
  status: SectionStatus
  onRetry: () => void
  /** Contra qué se compara lo que se mira: el promedio del período o, mirando una guardia, el de esa guardia. */
  reference: UrgencyDayReference
  /** Si una sola atención explica la espera media del día; null si no. */
  notice: OpenAttentionNotice | null
}

interface KpiProps {
  label: string
  value: ReactNode
  /** Renglones debajo: la cantidad detrás del porcentaje y la comparación. */
  details: ReactNode[]
}

function Kpi({ label, value, details }: KpiProps) {
  const shown = details.filter(Boolean)
  return (
    <div className="nv-urg-detail__kpi">
      <dt className="nv-urg-detail__kpi-label">{label}</dt>
      <dd className="nv-urg-detail__kpi-value">{value}</dd>
      {shown.length > 0 && (
        <dd className="nv-urg-detail__kpi-detail">
          {shown.map((detail, index) => <span key={index}>{detail}</span>)}
        </dd>
      )}
    </div>
  )
}

/** Un porcentaje (en %) como fracción, que es lo que compara `pointsVariation`. */
const toRatio = (percent: number | null): number | null => (percent === null ? null : percent / 100)

/**
 * Los indicadores del día: llegadas, espera hasta ser atendido, cuántos esperaron más de 1 hora y
 * cuántos se retiraron sin atención, cada uno contra el promedio del período de la pantalla o, mirando una guardia, contra el
 * promedio de esa guardia en el período (un día no se compara con «el período anterior», que sería el día de antes y puede
 * ser un fin de semana). Las llegadas y la espera se ven al
 * instante; el resto llega con la consulta del día. Si una sola atención explica la espera media, un aviso dice cuánto
 * sería la espera sin ella: puede ser una atención que no se cerró. Los pacientes en curso y los registros duplicados se
 * informan aparte, como en los indicadores del período.
 */
export default function UrgencyDayKpis({ day, kpis, status, onRetry, reference, notice }: UrgencyDayKpisProps) {
  const intl = useIntl()
  const referenceName = intl.formatMessage({ id: reference.nameId })
  const referenceText = (value: string) => intl.formatMessage({ id: 'UrgencySummaryDayVsReference' }, { name: referenceName, value })
  const line = (variation: Variation | null, value: string, verdict: Verdict = null): ReactNode =>
    (variation ? <ChangeLine variation={variation} period={referenceText(value)} verdict={verdict} /> : undefined)
  const patients = (count: number): string =>
    intl.formatMessage({ id: 'UrgencySummaryPatients' }, { count, countText: formatCount(intl, count) })

  const averageArrivals = reference.averageArrivals
  const arrivalsChange = averageArrivals !== null && averageArrivals > 0 ? percentVariation(day.arrivals, averageArrivals) : null
  const waitChange = day.avgWaitMinutes === null || reference.avgWaitMinutes === null
    ? null : percentVariation(day.avgWaitMinutes, reference.avgWaitMinutes)
  const over60Change = kpis ? pointsVariation(intl, toRatio(kpis.waitOver60Pct), toRatio(reference.waitOver60Pct)) : null
  const leftChange = kpis
    ? pointsVariation(intl, toRatio(kpis.leftWithoutAttentionPct), toRatio(reference.leftWithoutAttentionPct)) : null

  const loadingDetail = (
    <span key="loading" className="nv-loading" role="status">
      <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
      <span>{intl.formatMessage({ id: 'UrgencySummaryLoading' })}</span>
    </span>
  )
  const pendingValue = status === 'failed' ? '—' : '…'

  return (
    <section className="nv-urg-detail__section" aria-labelledby="nv-urg-detail-kpis-title">
      <h3 className="nv-section-title" id="nv-urg-detail-kpis-title">{intl.formatMessage({ id: 'UrgencySummaryDayKpisTitle' })}</h3>
      <dl className="nv-urg-detail__kpis">
        <Kpi
          label={intl.formatMessage({ id: 'UrgencySummaryKpiArrivals' })}
          value={formatCount(intl, day.arrivals)}
          details={[line(arrivalsChange, formatCount(intl, Math.round(averageArrivals ?? 0)))]}
        />
        <Kpi
          label={intl.formatMessage({ id: 'UrgencySummaryKpiWait' })}
          value={formatMinutes(intl, day.avgWaitMinutes)}
          details={[line(waitChange, formatMinutes(intl, reference.avgWaitMinutes), waitChange ? waitVerdict(waitChange.trend) : null)]}
        />
        <Kpi
          label={intl.formatMessage({ id: 'UrgencySummaryKpiWait60' })}
          value={kpis === null ? pendingValue : formatPercent(intl, kpis.waitOver60Pct)}
          details={kpis === null ? [status === 'loading' ? loadingDetail : undefined] : [
            kpis.waitedCount > 0 ? patients(kpis.waitOver60) : undefined,
            line(over60Change, formatPercent(intl, reference.waitOver60Pct), over60Change ? waitVerdict(over60Change.trend) : null),
          ]}
        />
        <Kpi
          label={intl.formatMessage({ id: 'UrgencySummaryKpiLeft' })}
          value={kpis === null ? pendingValue : formatPercent(intl, kpis.leftWithoutAttentionPct)}
          details={kpis === null ? [status === 'loading' ? loadingDetail : undefined] : [
            kpis.arrivals > 0 ? patients(kpis.leftWithoutAttention) : undefined,
            line(leftChange, formatPercent(intl, reference.leftWithoutAttentionPct), leftChange ? waitVerdict(leftChange.trend) : null),
            kpis.duplicates > 0 ? (
              <span className="nv-urg-ind__note">
                {intl.formatMessage(
                  { id: 'UrgencySummaryKpiDuplicates' },
                  { count: kpis.duplicates, countText: formatCount(intl, kpis.duplicates) },
                )}
                <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryKpiDuplicatesHint' })} />
              </span>
            ) : undefined,
          ]}
        />
      </dl>
      {status === 'failed' && (
        <div className="nv-urg-failed" role="status">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'UrgencySummaryDayKpisFailed' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'UrgencySummaryRetry' })} onClick={onRetry} />
        </div>
      )}
      {kpis !== null && <UrgencyInProgressNote count={kpis.inProgress} />}
      {notice && (
        <p className="nv-note nv-urg-detail__notice" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          <span>
            {intl.formatMessage({ id: 'UrgencySummaryDayOpenAttention' }, {
              wait: formatDuration(notice.maxWaitMinutes),
              average: formatDuration(notice.averageWithoutIt),
            })}
          </span>
        </p>
      )}
    </section>
  )
}
